import {t} from "@lingui/core/macro";
import {css, StyleSheet} from "aphrodite";
import moment from "moment";
import * as React from "react";
import {useRef} from "react";

import {View} from "@khanacademy/wonder-blocks-core";
import {color, spacing} from "@khanacademy/wonder-blocks-tokens";
import {useLocation} from "@khan/urls";

import Shimmer from "#components/shimmer-package/shimmer.tsx";
import useGetLearnerDashboardPromoEligibility from "#dashboard-promos-package/use-get-learner-dashboard-promo-eligibility.ts";
import useProfile from "#data-access/use-profile.ts";

import Error from "../content-pages/error.tsx";
import {
    getProfileRootPath,
    profilePathIsMine,
} from "../content-pages/profile-util.ts";
import {
    areGuideActivitiesAccessible,
    getSideNavLinks,
    isActivityAccessible,
    isCoachListReadable,
    isGuideHistoryAccessible,
    isGuideSettingsAccessible,
    isKhanmigoPromoAccessible,
    type HomepageQuery,
} from "../homepage-data.ts";

import ClassSections from "./class-sections.tsx";
import KhanmigoPromoSection from "./khanmigo-promo-section.tsx";
import Section from "./section.tsx";

type Props = {
    query: HomepageQuery;
    showError: boolean;
};

export default function SideNav({query, showError}: Props): React.ReactElement {
    const componentCreatedTimeRef = useRef(moment());
    const profileResult = useProfile();
    const location = useLocation();
    const profile =
        profileResult.status === "success" ? profileResult.profile : null;

    const profileRoot = getProfileRootPath(profile, location);
    const isMyPage = profilePathIsMine(profile, location);

    const promoEligibilityResult = useGetLearnerDashboardPromoEligibility();

    const navLinks = getSideNavLinks(
        query,
        profileRoot,
        componentCreatedTimeRef.current,
        {
            isMyPage,
            isActivityAccessible: isActivityAccessible(query),
            isCoachListReadable: isCoachListReadable(query),
            areGuideActivitiesAccessible: areGuideActivitiesAccessible(query),
            isGuideHistoryAccessible: isGuideHistoryAccessible(query),
            isGuideSettingsAccessible: isGuideSettingsAccessible(query),
            isKhanmigoPromoAccessible: isKhanmigoPromoAccessible(
                promoEligibilityResult,
            ),
        },
    );

    const isLoading =
        query.loading ||
        profileResult.status === "loading" ||
        promoEligibilityResult.status === "loading";

    return (
        <nav
            className={css(styles.nav)}
            data-testid="side-nav"
            aria-label={t`user profile navigation`}
        >
            <React.Fragment>
                {showError && (
                    <View style={styles.error}>
                        <Error />
                    </View>
                )}
                {!showError && isLoading && (
                    <Shimmer>
                        <View style={styles.skeleton} />
                        <View style={styles.skeleton} />
                        <View style={styles.skeleton} />
                        <View style={styles.skeleton} />
                    </Shimmer>
                )}

                {!showError && !isLoading && (
                    <>
                        {navLinks.classSection && (
                            <ClassSections
                                classSection={navLinks.classSection}
                                location={location}
                            />
                        )}
                        {navLinks.userSections.map((section, i) => (
                            <Section section={section} key={i.toString()} />
                        ))}
                        {navLinks.khanmigoPromoSection && (
                            <KhanmigoPromoSection
                                section={navLinks.khanmigoPromoSection}
                            />
                        )}
                    </>
                )}
            </React.Fragment>
        </nav>
    );
}

const styles = StyleSheet.create({
    nav: {
        height: "100%",
    },
    skeleton: {
        background: color.offBlack8,
        height: spacing.medium_16,
        marginTop: spacing.medium_16,
        marginRight: spacing.medium_16,
    },
    error: {
        margin: 4,
    },
});
